import { type OnboardingMetricsDto } from "@crowdcoursing/api/openapi";
import { Box, Link as MuiLink, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { useFeature } from "~hooks/useFeature";

import { selectMemberUsersTab, userManagementHref } from "./onboardingMembers";

const METRICS: { filter?: string; key: keyof OnboardingMetricsDto; label: string; requiresResumes?: boolean }[] = [
  { key: "totalUsers", label: "Total users" },
  { filter: "students", key: "students", label: "Students" },
  { filter: "candidates", key: "candidates", label: "Candidates", requiresResumes: true },
  { filter: "both", key: "both", label: "Both", requiresResumes: true },
  { filter: "studentOnly", key: "studentOnly", label: "Student-only", requiresResumes: true },
  { filter: "candidateOnly", key: "candidateOnly", label: "Candidate-only", requiresResumes: true },
  { filter: "candidateIncomplete", key: "candidateIncomplete", label: "Incomplete candidates", requiresResumes: true },
  { filter: "searchable", key: "searchable", label: "Searchable Candidates", requiresResumes: true },
  { filter: "admins", key: "admins", label: "Admins" },
];

export function OnboardingMetricsList({
  bubbleId,
  metrics,
}: {
  bubbleId: string;
  metrics: OnboardingMetricsDto | undefined;
}) {
  const showResumes = useFeature("showResumes");
  const visibleMetrics = METRICS.filter((metric) => showResumes || !metric.requiresResumes);

  return (
    <Box
      sx={{
        display: "grid",
        gap: 1,
        gridTemplateColumns: { sm: "repeat(3, minmax(0, 1fr))", xs: "repeat(2, minmax(0, 1fr))" },
      }}
    >
      {visibleMetrics.map((metric) => {
        const value = metrics?.[metric.key] ?? 0;
        const label = (
          <Typography color="text.secondary" variant="caption">
            {metric.label}
          </Typography>
        );
        const count = <Typography variant="h6">{value}</Typography>;
        return (
          <Box key={metric.key} data-testid={`onboardingMetric-${metric.key}-${bubbleId}`} sx={{ minWidth: 0 }}>
            <MuiLink
              component={RouterLink}
              sx={{ color: "inherit", display: "block", textDecoration: "none" }}
              to={userManagementHref(bubbleId, metric.filter)}
              underline="hover"
              onClick={selectMemberUsersTab}
            >
              {label}
              {count}
            </MuiLink>
          </Box>
        );
      })}
    </Box>
  );
}
